/**
 * One catalog unit, opened.
 *
 * What arrives here is the CLIENT payload — the same stripping the
 * serving path does, so the answer key, `optionFails` and the grant
 * declarations are absent.  A staff gate is a reason to trust the
 * reader, not a reason to widen what the wire carries; if reviewing
 * answer keys ever needs its own door, it should be built as one and
 * argued for on its own terms rather than arriving as a side effect of
 * this page.
 *
 * The instruments are HERE, nested, rather than on the shelf: a unit is made
 * of instruments, and a reviewer who cannot see them cannot review the
 * unit.  They just live inside the thing that is offered now, which is
 * where the author put them.
 */

import { useEffect, useState } from "react";

import type {
  Instrument,
  Opening,
  ReviewNote,
  Unit,
  UnitDetailPayload,
  Verdict,
} from "./types";
import type { Failure } from "./useLibrary";
import { recordNote } from "./useLibrary";
import {
  Badge,
  Button,
  Field,
  Id,
  Label,
  LevelMeter,
  LevelSpread,
  Menu,
  MenuItem,
  MONO,
  Panel,
  ReviewBadge,
} from "./ui";

function List({ values }: { values: string[] }) {
  if (!values.length) return <span style={{ color: "var(--color-text-muted)" }}>—</span>;
  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
      {values.map((value) => (
        <Badge key={value} mono>
          {value}
        </Badge>
      ))}
    </div>
  );
}

/**
 * Where this unit opens, and what stands in the way — the same pair the
 * registry admin shows, from the same server module, so the two
 * surfaces cannot disagree about a URL.
 *
 * A unit HAS a URL of its own, which is the whole point of the grain: it
 * is the thing that can be dealt.  Beside it is every other door into
 * the same vessel, because a reviewer reading one unit reaches its
 * siblings through that list and nowhere else.
 *
 * The blockers matter as much as the links.  A link with nothing beside
 * it is the worse half of the pair: a reviewer clicks it, gets nothing,
 * and cannot tell a broken page from content that was never meant to be
 * offerable yet.
 */
function OpensAt({
  row,
  detail,
  loading,
}: {
  row: Unit;
  detail: UnitDetailPayload | null;
  loading: boolean;
}) {
  const live = detail?.live ?? false;
  return (
    <div
      data-testid="unit-opens-at"
      style={{
        margin: "0 0 12px",
        padding: 10,
        borderRadius: 8,
        border: `1px solid ${live ? "var(--color-success-border)" : "var(--color-warning-border)"}`,
        background: live ? "var(--color-success-bg)" : "var(--color-warning-bg)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <Label>opens at</Label>
        <span style={{ flex: 1 }} />
        {detail ? (
          detail.live ? (
            <Badge tone="good">live</Badge>
          ) : (
            <Badge tone="warn">not live</Badge>
          )
        ) : (
          <Badge tone="neutral">{loading ? "loading…" : "—"}</Badge>
        )}
      </div>

      {/* The `#` is already percent-encoded server-side.  A raw one would
          be read as a fragment and dropped, which does not fail — it
          opens the vessel at its first unit and looks like it worked. */}
      <a
        data-testid="unit-url"
        href={row.url}
        target="_blank"
        rel="noreferrer"
        style={{
          fontFamily: MONO,
          fontSize: 11.5,
          color: "var(--color-primary)",
          wordBreak: "break-all",
        }}
      >
        {row.url}
      </a>

      {detail && detail.opensAt.length ? (
        <details data-testid="unit-vessel-doors" style={{ marginTop: 8 }}>
          <summary style={{ cursor: "pointer", fontSize: 12, color: "var(--color-text-secondary)" }}>
            every door into this vessel ({detail.opensAt.length})
          </summary>
          <ul style={{ listStyle: "none", margin: "6px 0 0", padding: 0, display: "grid", gap: 6 }}>
            {detail.opensAt.map((opening: Opening) => (
              <li key={opening.ref}>
                <a
                  data-testid={`opens-at-${opening.ref}`}
                  href={opening.url}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    fontFamily: MONO,
                    fontSize: 11.5,
                    color: "var(--color-primary)",
                    wordBreak: "break-all",
                  }}
                >
                  {opening.url}
                </a>
                {opening.title || opening.unit ? (
                  <div style={{ fontSize: 11.5, color: "var(--color-text-secondary)", marginTop: 1 }}>
                    {opening.title}
                    <span style={{ color: "var(--color-text-muted)" }}>
                      {opening.title ? " · " : ""}
                      {opening.unit ? `unit ${opening.unit}` : "the whole vessel"}
                      {opening.entry ? "" : " (not a cold entry)"}
                    </span>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        </details>
      ) : null}

      {detail && detail.blockers.length ? (
        <ul
          data-testid="unit-blockers"
          style={{
            margin: "8px 0 0",
            paddingLeft: 18,
            fontSize: 11.5,
            color: "var(--color-warning-text)",
          }}
        >
          {detail.blockers.map((blocker) => (
            <li key={blocker}>{blocker}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/**
 * The instruments this unit embeds — id, type, level, assessment, and behind a
 * disclosure what each one actually asks.
 *
 * One shape whether or not the registry has the row: an embedded id with
 * nothing published under it is a real defect, and a reader scanning the
 * instruments should meet it in the list rather than notice it missing from
 * one.
 */
function Instruments({ instruments }: { instruments: Instrument[] }) {
  if (!instruments.length) {
    return (
      <div style={{ fontSize: 12.5, color: "var(--color-text-muted)" }}>
        This unit embeds no priceable instrument — nothing inside it can be priced, which is legitimate
        for an experience and a defect for anything meant to be graded.
      </div>
    );
  }
  return (
    <div data-testid="unit-instruments" style={{ display: "grid", gap: 6 }}>
      {instruments.map((one) => (
        <InstrumentRow key={one.id} instrument={one} />
      ))}
    </div>
  );
}

function InstrumentRow({ instrument }: { instrument: Instrument }) {
  const spec = instrument.spec ?? null;
  const prompt = typeof spec?.prompt === "string" ? spec.prompt : "";
  const options = Array.isArray(spec?.options) ? (spec?.options as string[]) : [];
  const fails = Array.isArray(spec?.fails) ? (spec?.fails as { key: string; text: string }[]) : [];
  // Two declarations that ride the instrument's client payload rather than its
  // coordinates, and both are reviewable facts about the instrument: `observes`
  // is what it promises to measure about the learner's PROCESS (the gate
  // refuses any observable a close reports that this list does not
  // declare), and the exposure budget is how often it may be dealt
  // before it is spent.
  const observes = Array.isArray(spec?.observes) ? (spec?.observes as string[]) : [];
  const exposure = (spec?.exposure ?? null) as { budget?: number; cooldownDays?: number } | null;
  // A pin behind the latest published row is version SKEW — legitimate
  // and worth seeing, never silently reconciled.
  const skewed = instrument.pinned > 0 && instrument.version > 0 && instrument.pinned !== instrument.version;

  return (
    <details
      data-testid={`unit-instrument-${instrument.id}`}
      data-type={instrument.type}
      data-level={instrument.level}
      style={{
        padding: "6px 8px",
        borderRadius: 8,
        border: `1px solid ${instrument.missing ? "var(--color-error)" : "var(--color-border-subtle)"}`,
        background: "var(--color-bg-subtle)",
      }}
    >
      <summary style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <Id style={{ fontSize: 11.5, flex: 1, minWidth: 0 }}>{instrument.id}</Id>
        {instrument.missing ? (
          <Badge tone="bad" title="embedded by this vessel, with no registry row behind it">
            no registry row
          </Badge>
        ) : (
          <>
            <Badge mono title="type — the priceable instrument's shape">
              {instrument.type || "?"}
            </Badge>
            <LevelMeter level={instrument.level} />
            <Badge>{instrument.assessment || "no assessment"}</Badge>
          </>
        )}
        {instrument.tombstoned ? <Badge tone="bad">drained</Badge> : null}
        {skewed ? (
          <Badge tone="warn" title={`this vessel pins v${instrument.pinned}; the registry's latest is v${instrument.version}`}>
            pinned v{instrument.pinned}
          </Badge>
        ) : null}
      </summary>

      <div style={{ marginTop: 6, fontSize: 12 }}>
        {instrument.missing ? (
          <div style={{ color: "var(--color-error)" }}>
            Nothing is published under this id, so the unit cannot be served whole. Either the
            author renamed it or its file was never published.
          </div>
        ) : (
          <>
            <Field name="claims">
              <List values={instrument.claims} />
            </Field>
            {instrument.alsoMarks.length ? (
              <Field name="also marks">
                <List values={instrument.alsoMarks} />
              </Field>
            ) : null}
            <Field name="icap mode">{instrument.mode || "—"}</Field>
            <Field name="demonstrates">{instrument.demonstrates || "—"}</Field>
            <Field name="surface">
              {instrument.surface ? `${instrument.surface} (${instrument.surfaceDistance || "near"})` : "—"}
            </Field>
            <Field name="observes">
              {observes.length ? <List values={observes} /> : "—"}
            </Field>
            <Field name="exposure">
              {exposure?.budget
                ? `${exposure.budget} deal${exposure.budget === 1 ? "" : "s"}${
                    exposure.cooldownDays ? ` · ${exposure.cooldownDays}d cooldown` : ""
                  }`
                : "—"}
            </Field>
            <Field name="grants">
              {instrument.grants ? (
                <span>
                  {instrument.grants} placement declaration{instrument.grants === 1 ? "" : "s"}
                  <span style={{ color: "var(--color-text-muted)", fontSize: 11.5 }}>
                    {" "}
                    — the scopes are server-only and never shipped
                  </span>
                </span>
              ) : (
                "—"
              )}
            </Field>
            <Field name="version">
              v{instrument.version || "?"}
              {instrument.pinned ? (
                <span style={{ color: "var(--color-text-muted)" }}> · pinned v{instrument.pinned}</span>
              ) : null}
            </Field>
          </>
        )}

        {instrument.parseError ? (
          <div style={{ marginTop: 6, color: "var(--color-error)", fontFamily: MONO, fontSize: 11 }}>
            {instrument.parseError}
          </div>
        ) : null}

        {prompt ? (
          <div style={{ marginTop: 8 }}>
            <Label>prompt</Label>
            <div
              style={{
                marginTop: 4,
                padding: 8,
                borderRadius: 8,
                background: "var(--color-bg-raised)",
                fontSize: 12.5,
                whiteSpace: "pre-wrap",
              }}
            >
              {prompt}
            </div>
          </div>
        ) : null}

        {options.length ? (
          <div style={{ marginTop: 8 }}>
            <Label>options</Label>
            <ol style={{ margin: "4px 0 0", paddingLeft: 20 }}>
              {options.map((option, index) => (
                <li key={index} style={{ marginBottom: 3 }}>
                  {option}
                </li>
              ))}
            </ol>
            <div style={{ fontSize: 11, color: "var(--color-text-muted)", marginTop: 4 }}>
              which one is right is an answer key — server-only, not shipped here.
            </div>
          </div>
        ) : null}

        {fails.length ? (
          <div style={{ marginTop: 8 }}>
            <Label>named misconceptions</Label>
            <ul style={{ margin: "4px 0 0", paddingLeft: 18, fontSize: 12 }}>
              {fails.map((fail) => (
                <li key={fail.key} style={{ marginBottom: 3 }}>
                  <span style={{ fontFamily: MONO, fontSize: 11.5 }}>{fail.key}</span> — {fail.text}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </details>
  );
}

const VERDICTS: { verdict: Verdict; label: string; hint: string }[] = [
  { verdict: "reviewed", label: "Reviewed", hint: "I read this and I stand behind it" },
  { verdict: "flagged", label: "Flag", hint: "I read this and it needs work" },
  { verdict: "cleared", label: "Clear", hint: "retract the verdict — back to unread" },
];

function when(iso: string | null): string {
  if (!iso) return "";
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? "" : at.toLocaleString();
}

/**
 * The verdict on THIS unit, and the feedback behind it.
 *
 * Three buttons and a note, and the middle one is the point: a reviewer
 * who read something and found it wanting did the same work as one who
 * found it good, and a checkbox would have made the second verdict
 * unrecordable — quietly turning this into a page for approving things.
 *
 * The note is filed under the unit's PERMANENT name, so it survives the
 * address moving under a republish and detaches only when the unit row
 * it was pinned to is gone.
 */
function ReviewControls({
  row,
  detail,
  onSaved,
}: {
  row: Unit;
  detail: UnitDetailPayload | null;
  onSaved: () => void;
}) {
  const [note, setNote] = useState(row.reviewNote);
  const [busy, setBusy] = useState<Verdict | null>(null);
  const [failure, setFailure] = useState("");

  // A different unit is a different note.  Without this the textarea
  // carries the last one's feedback into the next, and the first person
  // to click "Reviewed" without looking down saves it against the wrong
  // unit.
  useEffect(() => {
    setNote(row.reviewNote);
    setFailure("");
  }, [row.permanentRef, row.reviewNote]);

  const history: ReviewNote[] = detail?.review?.history ?? [];

  const save = async (verdict: Verdict) => {
    setBusy(verdict);
    setFailure("");
    const sent = await recordNote(row.permanentRef, verdict, verdict === "cleared" ? "" : note);
    setBusy(null);
    if (sent.ok) onSaved();
    else setFailure(sent.detail);
  };

  return (
    <div
      data-testid="unit-review"
      style={{
        margin: "0 0 12px",
        padding: 12,
        borderRadius: 8,
        border: `1px solid ${
          row.review === "flagged"
            ? "var(--color-warning-border)"
            : row.review === "reviewed"
              ? "var(--color-success-border)"
              : "var(--color-border)"
        }`,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <Label>review</Label>
        <span data-testid="unit-review-state" style={{ display: "inline-flex", gap: 6 }}>
          <ReviewBadge state={row.review} />
          {row.review === "pending" ? (
            <Badge tone="neutral">nobody has read this</Badge>
          ) : null}
        </span>
        {row.reviewAuthor ? (
          <span style={{ fontSize: 11.5, color: "var(--color-text-muted)" }}>
            {when(detail?.review?.at ?? null)} · {row.reviewAuthor}
          </span>
        ) : null}
      </div>

      <textarea
        data-testid="unit-review-note"
        value={note}
        onChange={(event) => setNote(event.target.value)}
        placeholder="Feedback for the team that authored this — what to fix, what to keep."
        rows={2}
        style={{
          width: "100%",
          marginTop: 8,
          padding: "8px 10px",
          borderRadius: 8,
          border: "1px solid var(--color-border)",
          background: "var(--color-bg-subtle)",
          color: "var(--color-text)",
          fontFamily: "inherit",
          fontSize: 13,
          resize: "vertical",
          boxSizing: "border-box",
        }}
      />

      <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
        {VERDICTS.map(({ verdict, label, hint }) => (
          <span key={verdict} title={hint}>
            <Button
              testId={`unit-review-${verdict}`}
              kind={verdict === "reviewed" ? "primary" : "quiet"}
              disabled={busy !== null || (verdict === "cleared" && row.review === "pending")}
              onClick={() => save(verdict)}
            >
              {busy === verdict ? "saving…" : label}
            </Button>
          </span>
        ))}
      </div>

      {failure ? (
        <div data-testid="unit-review-error" style={{ marginTop: 8, fontSize: 12, color: "var(--color-error)" }}>
          {failure}
        </div>
      ) : null}

      {history.length ? (
        <details data-testid="unit-review-history" style={{ marginTop: 10 }}>
          <summary style={{ cursor: "pointer", fontSize: 12, color: "var(--color-text-muted)" }}>
            {history.length} earlier note{history.length === 1 ? "" : "s"} on content since replaced
          </summary>
          <ul style={{ margin: "8px 0 0", paddingLeft: 16, fontSize: 12 }}>
            {history.map((entry, index) => (
              <li key={index} style={{ marginBottom: 6, color: "var(--color-text-secondary)" }}>
                <b>{entry.verdict}</b>
                {entry.author ? ` · ${entry.author}` : ""}
                {entry.at ? ` · ${when(entry.at)}` : ""}
                {entry.note ? <div style={{ marginTop: 2 }}>{entry.note}</div> : null}
              </li>
            ))}
          </ul>
          <div style={{ fontSize: 11, color: "var(--color-text-muted)", marginTop: 6 }}>
            These were written against a unit row that has since been purged or superseded, so they
            are history — never the current verdict, and never exported.
          </div>
        </details>
      ) : null}
    </div>
  );
}

export function UnitDetail({
  row,
  detail,
  failure,
  loading,
  stacked,
  onClose,
  onReviewed,
  onRepublish,
}: {
  row: Unit | null;
  detail: UnitDetailPayload | null;
  failure: Failure | null;
  loading: boolean;
  /** One column: the panel reads as the next block, not a sidebar. */
  stacked: boolean;
  onClose: () => void;
  /** A verdict was saved — the shelf's counts are now stale. */
  onReviewed: () => void;
  /** The reader asked to replace the file this unit came from. */
  onRepublish: (filename: string) => void;
}) {
  if (!row) return null;
  const planetSkew = Boolean(row.vesselPlanet) && row.vesselPlanet !== row.planet;

  return (
    <Panel
      testId="unit-detail"
      style={{
        position: stacked ? "static" : "sticky",
        top: 16,
        maxHeight: stacked ? undefined : "calc(var(--app-100vh, 100vh) - 40px)",
        overflow: "auto",
        padding: 16,
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600 }}>{row.title || row.permanentRef}</div>
          {/* Both spellings, because they answer different questions: the
              address is what opens, the name is what the verdict is
              filed under and what survives a republish. */}
          <Id style={{ fontSize: 11.5, color: "var(--color-text-secondary)" }}>{row.unitRef}</Id>
          <div style={{ fontSize: 11, color: "var(--color-text-muted)", fontFamily: MONO }}>
            {row.permanentRef} · the permanent name
          </div>
          {row.hook ? (
            <div style={{ fontSize: 12.5, color: "var(--color-text-secondary)", marginTop: 6 }}>
              {row.hook}
            </div>
          ) : null}
          <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
            <Badge mono title="vesselType — the shape this unit is offered out of">
              {row.vesselType || "?"}
            </Badge>
            <LevelSpread levels={row.levels} />
            {row.offeredWhole ? (
              <Badge tone="accent" title="the author declared no units — offerable only whole">
                whole
              </Badge>
            ) : null}
            {row.entry ? null : <Badge tone="warn">no cold entry</Badge>}
            {row.tombstoned ? <Badge tone="bad">drained</Badge> : null}
          </div>
        </div>
        <button
          type="button"
          data-testid="unit-detail-close"
          aria-label="close"
          onClick={onClose}
          style={{
            width: 30,
            height: 30,
            borderRadius: 8,
            border: "1px solid var(--color-border)",
            background: "transparent",
            color: "var(--color-text-muted)",
            cursor: "pointer",
            fontSize: 14,
            lineHeight: 1,
          }}
        >
          ×
        </button>
      </div>

      {failure ? (
        <div style={{ color: "var(--color-error)", fontSize: 13 }}>
          {failure.kind === "error" ? failure.detail : "you may not read this unit"}
        </div>
      ) : null}

      {detail?.parseError ? (
        <div
          data-testid="unit-parse-error"
          style={{
            marginBottom: 12,
            padding: 10,
            borderRadius: 8,
            border: "1px solid var(--color-error)",
            color: "var(--color-error)",
            fontSize: 12,
          }}
        >
          This vessel's manifest no longer parses against the contract — which is itself the
          finding.
          <div style={{ fontFamily: MONO, marginTop: 6 }}>{detail.parseError}</div>
        </div>
      ) : null}

      <div style={{ opacity: loading ? 0.55 : 1 }}>
        <ReviewControls row={row} detail={detail} onSaved={onReviewed} />
        <OpensAt row={row} detail={detail} loading={loading} />

        <Field name="claim">
          <Id>{row.primaryClaim || "—"}</Id>
        </Field>
        {row.claims.length > 1 ? (
          <Field name="also tests">
            <List values={row.claims.slice(1)} />
          </Field>
        ) : null}
        {row.alsoMarks.length ? (
          <Field name="also marks">
            <List values={row.alsoMarks} />
          </Field>
        ) : null}
        {row.danglingClaims.length ? (
          <Field name="dangling">
            <div style={{ color: "var(--color-error)" }}>
              <List values={row.danglingClaims} />
              <div style={{ fontSize: 11.5, marginTop: 4 }}>no roster holds these claims</div>
            </div>
          </Field>
        ) : null}
        <Field name="cold entry">
          {row.entry
            ? "yes — the author allows this unit to be walked into first"
            : "no — the author's veto; reachable inside a whole-vessel serve or after its predecessor"}
        </Field>
        <Field name="minutes">{row.minutes || "—"}</Field>
        <Field name="spans">
          <List values={row.spans} />
        </Field>
        <Field name="marks">
          {/* How much of this unit is PRICED, which is the difference
              between content a learner is measured on and content they
              merely walk through. */}
          {row.gradedCount || row.experienceCount
            ? `${row.gradedCount} graded · ${row.experienceCount} experience`
            : "—"}
        </Field>
        {/* The three rolled-up axes a reviewer can FILTER on but could
            otherwise only find one instrument at a time.  Sets, not values —
            a bench section holding a judged rep beside a graded stake
            answers to both chips — and shown here so the row a chip
            returned can be checked against the chip that returned it. */}
        <Field name="assessment" title="any-of: every assessment its instruments declare">
          {row.assessments.length ? row.assessments.join(" · ") : "—"}
        </Field>
        <Field name="icap mode" title="any-of: every mode its instruments declare">
          {row.modes.length ? row.modes.join(" · ") : "—"}
        </Field>
        <Field name="demonstrates" title="any-of: every demonstrates its instruments declare">
          {row.demonstrates.length ? row.demonstrates.join(" · ") : "—"}
        </Field>
        <Field name="grants">
          {row.grants ? (
            <span>
              {row.grants} placement declaration{row.grants === 1 ? "" : "s"}
              <span style={{ color: "var(--color-text-muted)", fontSize: 11.5 }}>
                {" "}
                — the scopes are server-only and never shipped
              </span>
            </span>
          ) : (
            "—"
          )}
        </Field>
        <Field name="planet">
          <Id>{row.planet || "—"}</Id>
          {row.ring ? (
            <span style={{ color: "var(--color-text-muted)" }}> · ring {row.ring}</span>
          ) : (
            <span style={{ color: "var(--color-warning-text)" }}> · outside the rings</span>
          )}
          {/* The library resolves the planet from the primary claim and
              keeps the declaration beside it, because the two
              disagreeing is a defect the declaration would have hidden. */}
          {planetSkew ? (
            <div style={{ color: "var(--color-error)", fontSize: 11.5, marginTop: 2 }}>
              the manifest declares <span style={{ fontFamily: MONO }}>{row.vesselPlanet}</span>
            </div>
          ) : null}
        </Field>
        <Field name="from">
          {row.sourceFile ? (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <Id>{row.sourceFile}</Id>
              {/* The file is the unit the press works in, so the action
                  that replaces it belongs beside the file's name rather
                  than in a menu somewhere else on the page. */}
              <Menu label="⋯" testId="source-file-actions" align="right">
                {(close) => (
                  <MenuItem
                    testId="source-file-republish"
                    onClick={() => {
                      close();
                      onRepublish(row.sourceFile);
                    }}
                  >
                    Purge &amp; republish this file…
                  </MenuItem>
                )}
              </Menu>
            </span>
          ) : (
            "—"
          )}
        </Field>
        <Field name="published">
          {detail?.publishedAt || row.publishedAt
            ? new Date(detail?.publishedAt || row.publishedAt).toLocaleString()
            : "—"}
        </Field>
        <Field name="versions">
          {detail ? detail.publishedVersions.join(", ") : row.versions}
        </Field>
        <Field name="provenance">
          {row.provenance}
          {detail?.mintedBy ? ` · ${detail.mintedBy}` : ""}
        </Field>
        {detail?.contentHash ? (
          <Field name="content hash">
            <Id style={{ fontSize: 11 }}>{detail.contentHash}</Id>
          </Field>
        ) : null}
        {detail && detail.siblings.length > 1 ? (
          <Field name="siblings">
            <List values={detail.siblings} />
          </Field>
        ) : null}

        <div style={{ marginTop: 14 }}>
          <Label>
            instruments ({row.instruments.length})
            {row.types.length ? ` · ${row.types.join(", ")}` : ""}
          </Label>
          <div style={{ marginTop: 6 }}>
            {detail ? (
              <Instruments instruments={detail.instruments} />
            ) : (
              <div style={{ fontSize: 12.5, color: "var(--color-text-muted)" }}>
                {loading ? "loading the instruments…" : <List values={row.instruments} />}
              </div>
            )}
          </div>
        </div>
      </div>
    </Panel>
  );
}
